import http, { ApiRequestConfig } from "@services/httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import {
  ContractEventModel,
  ContractEventUpdateModel,
  CustomerContractEventModel,
} from "@models/contract/contractModel"
import { ContractEventTypeEnum, RequestStatusEnum } from "@lib/enum"
import type {
  IContractEventFeeBatchResult,
  IPinkBookDueDateBatchInput,
  IPinkBookPhaseInfo,
} from "@models/contract/contractEventPinkBookFeeModel"
import { downloadFile } from "@lib/helperFile"
import dayjs from "dayjs"

export enum ContractEventImportKind {
  Transfer = "transfer",
  Convert = "convert",
  Cancel = "cancel",
  InterestReduction = "interestReduction",
  RegistrationFee = "registrationFee",
  /** Feature-0382 — sự kiện "Thay đổi chính sách bán hàng". */
  UpdateSalesPolicy = "updateSalesPolicy",
}

/**
 * `validateUrl` chỉ có ở kind dùng lưới xem trước (`ReviewImportExcelV2`); kind còn lại để `undefined`
 * và đi qua `ImportModal` cũ.
 *
 * Feature-0382 — `UpdateSalesPolicy` dùng CÙNG file mẫu 19 cột và CÙNG tên tệp với `Convert`: bytes y hệt
 * nhau, đặt hai tên khác nhau chỉ làm người dùng hỏi "file nào mới đúng". Khác biệt duy nhất giữa hai kind
 * là `importUrl`/`validateUrl` — backend đọc loại sự kiện được phép mang TỪ route, nên frontend không phải
 * gửi thêm tham số nào.
 */
const CONTRACT_EVENT_IMPORT_CONFIG: Record<
  ContractEventImportKind,
  {
    importUrl: string
    templateUrl: string
    templateFileName: string
    validateUrl?: string
  }
> = {
  [ContractEventImportKind.Transfer]: {
    importUrl: "api/Imports/ImportContractEventTransfer",
    templateUrl: "api/Imports/GetContractEventTransferTemplate",
    templateFileName: "Import-ContractEvent-Transfer-Template.xlsx",
  },
  [ContractEventImportKind.Convert]: {
    importUrl: "api/Imports/ImportContractEventConvert",
    validateUrl: "api/Imports/ContractEvent/ValidateBeforeImportConvert",
    templateUrl: "api/Imports/GetContractEventConvertTemplate",
    templateFileName: "Import-ContractEvent-Convert-Template.xlsx",
  },
  [ContractEventImportKind.UpdateSalesPolicy]: {
    // Feature-0383 — luồng riêng 19 cột salespolicy (cặp Loại/Giá trị, khuyến mãi %/số tiền). Route + file
    // mẫu KHÁC Convert: bố cục cột khác nên không dùng chung reader/job/template được.
    importUrl: "api/Imports/ImportContractEventSalesPolicy",
    validateUrl: "api/Imports/ContractEvent/ValidateBeforeImportSalesPolicy",
    templateUrl: "api/Imports/GetContractEventSalesPolicyTemplate",
    templateFileName: "Import-ContractEvent-Convert-SalePolicy.xlsx",
  },
  [ContractEventImportKind.Cancel]: {
    importUrl: "api/Imports/ImportContractEventCancel",
    templateUrl: "api/Imports/GetContractEventCancelTemplate",
    templateFileName: "Import-ContractEvent-Cancel-Template.xlsx",
  },
  [ContractEventImportKind.InterestReduction]: {
    importUrl: "api/Imports/ImportContractEventInterestReduction",
    templateUrl: "api/Imports/GetContractEventInterestReductionTemplate",
    templateFileName: "Import-ContractEvent-InterestReduction-Template.xlsx",
  },
  [ContractEventImportKind.RegistrationFee]: {
    importUrl: "api/Imports/ImportContractEventRegistrationFee",
    templateUrl: "api/Imports/GetContractEventRegistrationFeeTemplate",
    templateFileName: "Import-ContractEvent-RegistrationFee-Template.xlsx",
  },
}

/**
 * Feature-0377 — hai loại sự kiện duy nhất mà `api/Export/ExportContractEventConvert` phục vụ.
 * Bộ lọc của màn cho chọn cả 13 loại, còn BE thì LOẠI BỎ giá trị ngoài hai loại này và coi
 * "rỗng sau khi loại" là "không lọc" ⇒ user đang lọc "Chuyển nhượng khách hàng" mà bấm Xuất sẽ
 * nhận file chuyển đổi. Vì vậy caller phải chặn trước khi gọi (xem `contractEventExportButton`).
 */
export const CONVERT_EXPORT_TYPES: ContractEventTypeEnum[] = [
  ContractEventTypeEnum.DA2SPA,
  ContractEventTypeEnum.DA2LTL,
]

/**
 * Feature-0384 — loại sự kiện duy nhất mà `api/Export/ExportContractEventSalesPolicy` phục vụ.
 *
 * Khác `CONVERT_EXPORT_TYPES`: endpoint này KHÔNG fallback "xuất tất cả" khi bộ lọc loại không giao
 * (BE trả 204), nên việc chặn ở FE là để khỏi bắn request rỗng + hiện đúng thông điệp, không phải để
 * tránh tải file sai.
 */
export const SALES_POLICY_EXPORT_TYPES: ContractEventTypeEnum[] = [
  ContractEventTypeEnum.UpdateSalesPolicy,
]

/** Query param của `GET api/Export/ExportContractEventConvert` — trùng tên với bộ lọc `GetAllEvents`. */
export interface IExportContractEventConvertFilter {
  projectId?: number
  contractId?: number
  keyword?: string
  eventDate?: string
  status?: RequestStatusEnum
  types?: ContractEventTypeEnum[]
}

class ContractEventService {
  private async postContractEventImport(url: string, file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    const result = await http.post(url, formData, {
      headers: { "content-type": "multipart/form-data" },
    })
    if (result.data?.success === false) {
      const messageKey = result.data?.error?.message || "IncorrectExcelFormat"
      notifyError(LNotification("ERROR"), L(messageKey))
      throw new Error(messageKey)
    }
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("IMPORT_IS_BEING_PROCESSED")
    )
    return result.data?.result
  }

  public async importContractEventExcel(
    kind: ContractEventImportKind,
    file: File
  ): Promise<any> {
    const { importUrl } = CONTRACT_EVENT_IMPORT_CONFIG[kind]
    return this.postContractEventImport(importUrl, file)
  }

  public async downloadContractEventImportTemplate(
    kind: ContractEventImportKind
  ): Promise<void> {
    const { templateUrl, templateFileName } = CONTRACT_EVENT_IMPORT_CONFIG[kind]
    const res = await http.get(templateUrl, { responseType: "blob" })
    downloadFile(res.data, templateFileName)
  }
  public async get(id: number): Promise<any> {
    if (!id) {
      notifyError(L("ERROR"), L("ENTITY_NOT_FOUND"))
    }
    const res = await http.get("api/services/app/ContractEvent/GetEvent", {
      params: { id },
    })
    const { result } = res.data
    return ContractEventModel.assign(result)
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/ContractEvent/GetAllEvents", {
      params,
    })
    return res.data.result
  }
  public async getHistoryContract(id: any): Promise<any> {
    const res = await http.get(
      "api/services/app/ContractEvent/GetHistoryContract",
      {
        params: { id },
        // Sự kiện chưa có dòng ContractEventVersioning → BE trả EntityNotFound kèm
        // message kỹ thuật. Caller tự hiện cảnh báo thân thiện thay cho popup đó.
        skipErrorNotification: true,
      } as ApiRequestConfig
    )
    return res.data.result
  }
  // CONTRACT EVENT

  public async createOrUpdate(body: any, notNotify = false) {
    let url = ""
    switch (body.type) {
      case ContractEventTypeEnum.DACancel:
        url = "api/services/app/ContractEvent/CreateOrUpdateCancelDA"
        break
      case ContractEventTypeEnum.DA2SPA:
        url = "api/services/app/ContractEvent/CreateOrUpdateTransferDAToSPA"
        break
      case ContractEventTypeEnum.DA2LTL:
        url = "/api/services/app/ContractEvent/CreateOrUpdateTransferDAToLTL"
        break
      case ContractEventTypeEnum.DATransfer:
        url = "api/services/app/ContractEvent/CreateOrUpdateTransferDA"
        break
      case ContractEventTypeEnum.SPACancel:
        url = "api/services/app/ContractEvent/CreateOrUpdateCancelSPA"
        break
      case ContractEventTypeEnum.SPATransfer:
        url = "api/services/app/ContractEvent/CreateOrUpdateTransferSPA"
        break
      case ContractEventTypeEnum.LTLCancel:
        url = "api/services/app/ContractEvent/CreateOrUpdateCancelLTL"
        break
      case ContractEventTypeEnum.LTLTransfer:
      case ContractEventTypeEnum.LTL2SPA:
        url = "api/services/app/ContractEvent/CreateOrUpdateTransferLTL"
        break

      case ContractEventTypeEnum.ChangePaymentTerm:
        url = "api/services/app/ContractEvent/ChangePaymentTerm"
        break
      case ContractEventTypeEnum.Discount:
        url = "api/services/app/ContractEvent/CreateOrUpdateDiscount"
        break
      case ContractEventTypeEnum.InterestReduction:
        url = "api/services/app/ContractEvent/CreateOrUpdateInterestReduction"
        break
      case ContractEventTypeEnum.UpdateContractDates:
        url = "api/services/app/ContractEvent/CreateOrUpdateContractDates"
        break
      case ContractEventTypeEnum.UpdateSalesPolicy:
        url = "api/services/app/ContractEvent/CreateOrUpdateSalesPolicy"
        break
      default:
        notifyError(L("ERROR"), L("CONTRACT_EVENT_TYPE_NOT_FOUND"))
        return
    }

    const newObj = ContractEventUpdateModel.assign(body)
    const res = await http.post(url, newObj)
    {
      !notNotify &&
        notifySuccess(
          LNotification("SUCCESS"),
          LNotification(
            body?.id
              ? "UPDATE_CONTRACT_EVENT_SUCCESSFULLY"
              : "CREATE_CONTRACT_EVENT_SUCCESSFULLY"
          )
        )
    }
    return res.data.result
  }

  /**
   * Feature-0204 — tra đợt cấp sổ hồng + phí hành chính hiện có của một danh sách hợp đồng.
   * Là POST dù chỉ để đọc: `contractIds` có thể vài trăm phần tử, không truyền qua query string.
   */
  public async resolvePinkBookPhaseInfo(
    contractIds: number[]
  ): Promise<IPinkBookPhaseInfo[]> {
    const res = await http.post(
      "api/services/app/ContractEvent/ResolvePinkBookPhaseInfo",
      { contractIds }
    )
    return res.data.result ?? []
  }

  /**
   * Feature-0204 — tạo lô sự kiện Cập nhật hạn đợt cấp sổ hồng. Lô trải nhiều dự án được BE
   * tách thành một phiếu duyệt cho mỗi dự án, và một phần hợp đồng có thể bị từ chối trong khi
   * HTTP vẫn thành công ⇒ caller phải render cả `failedContracts`, KHÔNG chỉ toast success.
   */
  public async createPinkBookDueDateEvents(
    body: IPinkBookDueDateBatchInput
  ): Promise<IContractEventFeeBatchResult> {
    const res = await http.post(
      "api/services/app/ContractEvent/CreatePinkBookDueDateEvents",
      body
    )
    return res.data.result
  }

  /**
   * Feature-0377 — xuất sự kiện chuyển đổi hợp đồng (DA→SPA / DA→LTL) ra Excel 2 sheet
   * (`Data` nạp lại được qua import, `Detail` là lịch thanh toán đề xuất, chỉ để đọc).
   *
   * Trả về `false` khi BE báo `204 No Content` (không dòng nào khớp bộ lọc). Axios KHÔNG ném lỗi
   * với 204, nên đưa thẳng `res.data` cho `downloadFile` sẽ tải về tệp .xlsx 0 byte — Excel báo
   * hỏng mà không toast, không log. Caller hiện cảnh báo thay vì tải tệp.
   */
  public async exportConvertEvents(
    params: IExportContractEventConvertFilter
  ): Promise<boolean> {
    const res = await http.get("api/Export/ExportContractEventConvert", {
      params,
      responseType: "blob",
    })

    if (res.status === 204) return false

    // BE truyền đường dẫn template làm `fileName` của `FileResult` nên tên tệp phải do FE đặt.
    downloadFile(
      res.data,
      `EXPORT-CONTRACT-EVENT-CONVERT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
    notifySuccess(LNotification("SUCCESSFULLY"), LNotification("EXPORT_SUCCESSFULLY"))
    return true
  }

  /**
   * Feature-0384 — xuất sự kiện "Cập nhật chính sách bán hàng" ra Excel 2 sheet: `Data` là bố cục 19
   * cột salespolicy (5 cặp Loại/Giá trị khuyến mãi) nạp lại được qua *Nhập* → *Thay đổi chính sách bán
   * hàng*, `Detail` là lịch thanh toán đề xuất của từng phiếu, chỉ để đọc.
   *
   * Trả `false` khi BE báo 204 — xem chú thích của `exportConvertEvents`.
   */
  public async exportSalesPolicyEvents(
    params: IExportContractEventConvertFilter
  ): Promise<boolean> {
    const res = await http.get("api/Export/ExportContractEventSalesPolicy", {
      params,
      responseType: "blob",
    })

    if (res.status === 204) return false

    // BE truyền đường dẫn template làm `fileName` của `FileResult` nên tên tệp phải do FE đặt.
    downloadFile(
      res.data,
      `EXPORT-CONTRACT-EVENT-SALES-POLICY-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`
    )
    notifySuccess(LNotification("SUCCESSFULLY"), LNotification("EXPORT_SUCCESSFULLY"))
    return true
  }

  /**
   * Feature-0371 — xem trước tệp nhập sự kiện chuyển đổi: BE kiểm ĐỒNG BỘ và trả `{ datas, errors }`
   * đúng shape mà `ReviewImportExcelV2` đọc.
   *
   * `datas` DÀY và cùng thứ tự với dòng Excel từ dòng 3 (phần tử `i` ↔ dòng `i + 3`) — component không
   * tự đọc tệp, nó lấy nguyên `datas` làm nguồn lưới và tra ô lỗi theo `rowIndex + startRow`.
   * KHÔNG set `content-type` thủ công để Axios tự gắn boundary của multipart.
   *
   * Feature-0382 — URL lấy từ `CONTRACT_EVENT_IMPORT_CONFIG[kind].validateUrl` thay vì hằng cứng, đối xứng
   * với `importContractEventExcel(kind, file)`. Nhờ vậy mục menu thứ hai (thay đổi CSBH) không cần một hàm
   * gần-giống thứ hai, và route đã mang sẵn loại sự kiện được phép nhập.
   */
  public async validateBeforeImportConvert(
    kind: ContractEventImportKind,
    file: File
  ): Promise<{ datas: any[]; errors: any[] }> {
    const { validateUrl } = CONTRACT_EVENT_IMPORT_CONFIG[kind]
    if (!validateUrl) {
      throw new Error(`Import kind '${kind}' does not support preview validation`)
    }

    const formData = new FormData()
    formData.append("file", file)
    const res = await http.post(validateUrl, formData)
    return res.data.result
  }

  // change payment term
  public async exportPaymentTerm(params: any): Promise<any> {
    const res = await http.get("api/Export/ExportChangePaymentTerm", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-PAYMENT-TERM-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
    notifySuccess(LNotification("SUCCESSFULLY"), LNotification("EXPORT_SUCCESSFULLY"))
  }

  public async importPaymentTerm(contractId: number, file: File): Promise<any> {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("contractId", contractId.toString())

    const result = await http.post(
      "api/Imports/ContractEvent/ImportChangePaymentTerm",
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )

    if (!!result.data.result.errors && result.data.result.errors?.length > 0) {
      // Feature-0370 — lỗi cấp FILE (vd tập cờ LUF ở trạng thái 2..n−1 đợt) có `cell = null`:
      // gộp theo `cell` không thì thông báo rỗng, người dùng không biết vì sao import fail.
      const errorMessage = result.data.result.errors
        ?.map((i) => i?.cell || i?.message)
        .filter(Boolean)
        .join(", ")
      notifyError(
        LNotification("ERROR"),
        LNotification("IMPORT_PAYMENT_TERM_FAILED_{0}", errorMessage)
      )
    } else {
      notifySuccess(
        LNotification("SUCCESS"),
        LNotification("IMPORT_PAYMENT_TERM_SUCCESSFULLY")
      )
    }
    return result.data.result
  }

  /*
  Generate Print Document
  * dùng cho DA to SPA, DA to LTL, LTL to SPA, SPA to LTL
  * body: {contractEventId: number, templateMapId: number}
  */
  public async GeneratePrintDocument(body: any) {
    const res = await http.post("/api/services/app/ContractEvent/GeneratePrintDocument", body)
    return res.data.result
  }
  /*
    Generate Print Document
    * dùng cho tất cả những thằng nào transfer khách hàng , ví dụ DATransfer, SPA transfer, LTL transfer
    * body: {contractEventId: number, templateMapId: number}
    */
  public async GeneratePrintDocumentTransfer(body: any) {
    const res = await http.post("/api/services/app/ContractEvent/GeneratePrintDocumentTransfer", body)
    return res.data.result
  }

  /*
    Generate Print Document
    * dùng cho Thanh lý
    * body: {contractEventId: number, templateMapId: number}
    */
  public async GeneratePrintDocumentLiquidation(body: any) {
    const res = await http.post("/api/services/app/ContractEvent/GeneratePrintDocumentLiquidation", body)
    return res.data.result
  }

  /** Feature-0381 — sửa RIÊNG khối khách hàng của sự kiện chưa duyệt xong. */
  public async updateEventCustomers(contractEventId: number, customers: any[]) {
    const normalizedCustomers = CustomerContractEventModel.assigns(customers)
    const res = await http.put(
      "api/services/app/ContractEvent/UpdateEventCustomers",
      { contractEventId, customers: normalizedCustomers }
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return res.data.result
  }

}

export default new ContractEventService()
